// frontend/src/components/AccountSettings.js - Themed version with professional design
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import { useChangeTracker } from '../utils/changeTracker';
import { User, Mail, Phone, Trash2, Save, AlertTriangle, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import { isInternationalNumber } from '../utils/phoneUtils';

const AccountSettings = () => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [currentEntityId] = useState(localStorage.getItem('entityId') || '');

  // Change tracking for profile
  const profileTracker = useChangeTracker({});

  useEffect(() => {
    fetchProfile();
  }, []);
   
  const fetchProfile = async () => {
    try {
      if (!currentEntityId) {
        toast.error('No entity selected');
        return;
      }
      
      const response = await axios.get(`/api/settings/${currentEntityId}/profile`);
      if (response.data.success) {
        const fetchedProfile = response.data.data;
        
        // Ensure all fields are strings (not null) to avoid React warning
        const sanitizedProfile = {
          username: fetchedProfile.username || '',
          email: fetchedProfile.email || '',
          first_name: fetchedProfile.first_name || '',
          last_name: fetchedProfile.last_name || '',
          phone: fetchedProfile.phone || ''
        };

        setProfile(sanitizedProfile);
        // Update original values for change tracking
        profileTracker.updateOriginalValues(sanitizedProfile);
      }
    } catch (error) {
      toast.error(t('errorFetchingProfile'));
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Get only changed fields (exclude username as it's not editable)
      const editableFields = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        phone: profile.phone
      };
      
      const changedFields = profileTracker.getChangedFields(editableFields);
      
      // Only make API call if there are changes
      if (Object.keys(changedFields).length > 0) {
        if (!currentEntityId) {
          toast.error('No entity selected');
          return;
        }
        
        console.log('Saving changed profile fields:', changedFields);
        
        const response = await axios.put(`/api/settings/${currentEntityId}/profile`, changedFields);

        if (response.data.success) {
          // Update original values after successful save
          profileTracker.updateOriginalValues(editableFields);
          
          toast.success(t('profileUpdatedSuccessfully'));
        }
      } else {
        toast.info(t('noChangesToSave'));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await axios.delete(`/api/settings/${currentEntityId}/account`, {
        data: { password: deletePassword }
      });

      if (response.data.success) {
        toast.success(t('accountDeletedSuccessfully'));
        localStorage.clear();
        window.location.href = '/login';
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorDeletingAccount'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn("text-2xl font-bold", text.primary)}>{t('myAccount')}</h2>
        <p className={cn("mt-1 text-sm", text.muted)}>{t('manageYourPersonalInformation')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <ThemedCard>
          <ThemedCard.Header>{t('personalInformation')}</ThemedCard.Header>
          <ThemedCard.Body>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <ThemedInput
                label={
                  <>
                    <User className="inline w-4 h-4 mr-1" />
                    {t('username')}
                  </>
                }
                name="username"
                value={profile.username}
                disabled
                helperText={t('usernameCannotBeChanged')}
              />

              <ThemedInput
                label={
                  <>
                    <Mail className="inline w-4 h-4 mr-1" />
                    {t('email')}
                  </>
                }
                type="email"
                name="email"
                value={profile.email}
                onChange={handleInputChange}
                required
              />

              <ThemedInput
                label={t('firstName')}
                name="first_name"
                value={profile.first_name}
                onChange={handleInputChange}
              />

              <ThemedInput
                label={t('lastName')}
                name="last_name"
                value={profile.last_name}
                onChange={handleInputChange}
              />

              <ThemedInput
                label={
                  <>
                    <Phone className="inline w-4 h-4 mr-1" />
                    {t('phone')}
                  </>
                }
                type="tel"
                name="phone"
                value={profile.phone}
                onChange={handleInputChange}
                placeholder={t('phoneNumberPlaceholder')}
                helperText={t('phoneChargeWarning')}
              />
              {/* International phone warning — informational only, does not block saving */}
              {profile.phone && isInternationalNumber(profile.phone) && (
                <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{t('smsInternationalInfo')}</span>
                </div>
              )}
            </div>
          </ThemedCard.Body>
          <ThemedCard.Footer>
            <div className="flex justify-end">
              <ThemedButton
                type="submit"
                disabled={saving}
                loading={saving}
                icon={Save}
              >
                {t('saveChanges')}
              </ThemedButton>
            </div>
          </ThemedCard.Footer>
        </ThemedCard>
      </form>

      {/* Danger Zone */}
      <div className={cn(
        "border rounded-lg p-6",
        "bg-red-50 dark:bg-red-900/20",
        "border-red-200 dark:border-red-800"
      )}>
        <h3 className={cn("text-lg font-medium mb-4", "text-red-900 dark:text-red-200")}>
          <AlertTriangle className="inline w-5 h-5 mr-2" />
          {t('dangerZone')}
        </h3>

        <div className="space-y-4">
          <div>
            <h4 className={cn("text-sm font-medium", "text-red-800 dark:text-red-300")}>
              {t('deleteAccount')}
            </h4>
            <p className={cn("mt-1 text-sm", "text-red-600 dark:text-red-400")}>
              {t('deleteAccountWarning')}
            </p>
            <ThemedButton
              variant="danger"
              onClick={() => setShowDeleteModal(true)}
              icon={Trash2}
              className="mt-3"
            >
              {t('deleteMyAccount')}
            </ThemedButton>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className={cn(
          "fixed inset-0 flex items-center justify-center p-4 z-50",
          background.overlay
        )}>
          <ThemedCard className="max-w-md w-full">
            <ThemedCard.Header>{t('confirmAccountDeletion')}</ThemedCard.Header>
            <ThemedCard.Body>
              <p className={cn("text-sm mb-4", text.secondary)}>
                {t('deleteAccountFinalWarning')}
              </p>
              <ThemedInput
                label={t('enterPassword')}
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder={t('password')}
              />
            </ThemedCard.Body>
            <ThemedCard.Footer>
              <div className="flex justify-end space-x-3">
                <ThemedButton
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                  }}
                >
                  {t('cancel')}
                </ThemedButton>
                <ThemedButton
                  variant="danger"
                  onClick={handleDeleteAccount}
                >
                  {t('deleteAccount')}
                </ThemedButton>
              </div>
            </ThemedCard.Footer>
          </ThemedCard>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
